<template>
    <form-validation ref="exposedValidation">
        <modal-dialog
            ref="addFaqForm"
            label="faq"
            description="Modal for adding a new faq"
        >
            <template #default>
                <text-heading>Voeg een FAQ toe:</text-heading>
                <horizontal-line />
                <flex-container>
                    <input-group v-model="faqForm.question">
                        <template #label>
                            <input-label>
                                Vraag
                            </input-label>
                        </template>
                        <template #input>
                            <text-input />
                        </template>
                    </input-group>
                    <input-group v-model="faqForm.answer">
                        <template #label>
                            <input-label>
                                Antwoord
                            </input-label>
                        </template>
                        <template #input>
                            <text-area-input height="30vh" />
                        </template>
                    </input-group>
                </flex-container>
            </template>
            <template #footer="{ bindings }">
                <dialog-footer>
                    <button-block
                        :disabled="!exposedValidation?.formValidationBindings.isValid.value"
                        without-loading
                        @click="submitFaq(bindings)"
                    >
                        Toevoegen
                    </button-block>
                </dialog-footer>
            </template>
        </modal-dialog>
    </form-validation>
    <flex-container>
        <div class="faq-form">
            <div>
                <flex-container :padding="Size.sm">
                    <text-decorator :size="FontSize.sm" weight="medium">
                        Voeg Q&A's toe om de A.I. Medewerker te helpen sneller en nauwkeuriger vragen van klanten te beantwoorden
                    </text-decorator>
                </flex-container>
            </div>
            <button-block
                :color="'white'"
                without-loading
                @click="addFaqForm?.toggleOverlay()"
            >
                <svg-icon
                    :icon="AddIcon"
                    :color="'primary'"
                    is-custom-icon
                />
                <text-decorator :color="'primary'">
                    FAQ toevoegen
                </text-decorator>
            </button-block>
        </div>

        <horizontal-line no-margin />

        <flex-container>
            <flex-container justify-content="flex-end">
                <div class="faq-toggle">
                    <text-decorator :size="FontSize.xs" weight="medium">
                        FAQ's toedienen:
                    </text-decorator>
                    <flex-container size="auto">
                        <input-group
                            v-model="assistent.behavior.faqsEnabled"
                            inline
                        >
                            <template #input>
                                <switch-input />
                            </template>
                        </input-group>
                    </flex-container>
                    <text-decorator :size="FontSize.xs">
                        {{ assistent.behavior.faqsEnabled ? 'Ingeschakeld' : 'Uitgeschakeld' }}
                    </text-decorator>
                </div>
            </flex-container>
        </flex-container>

        <faq-list
            :faqs="assistent.behavior.faqs"
            @delete:faq="deleteFaq"
            @save:faq="saveFaq"
        />
    </flex-container>
</template>

<script setup lang="ts">
    import {
        type ExposedFormValidationBindings,
        type ExposedOverlay,
        FlexContainer,
        FontSize,
        FormValidation,
        InputGroup,
        NotificationBlock,
        Size,
        SwitchInput,
        TextAreaInput,
        TextDecorator,
        useNotify,
    } from '@keyvue/one-ui';
    import { type Assistent, Faq, FaqId } from '@keyvue/one-api-client';
    import { type Ref, ref, watch } from 'vue';
    import AddIcon from '@keyvue/one-awesome-icons/icons/regular/plus.svg?component';
    import FaqList from '@/ai/assistent/components/forms/knowledge/FaqList.vue';
    import { v4 as uuid } from 'uuid';

    const props = defineProps<{ modelValue: Assistent }>();
    const emit = defineEmits<{
        (e: 'update:modelValue', value: Assistent): void;
    }>();

    const assistent = ref(props.modelValue) as Ref<Assistent>;

    const addFaqForm = ref<ExposedOverlay>();

    const { notify } = useNotify();

    export interface Form {
        question: string | null,
        answer: string | null,
    }

    const faqForm = ref<Form>({
        question: null,
        answer: null,
    });

    const exposedValidation = ref<ExposedFormValidationBindings>();

    watch(props.modelValue, (value) => {
        assistent.value = value;
    });

    watch(assistent, (value: Assistent) => {
        emit('update:modelValue', value);
    }, { deep: true });

    function submitFaq(bindings: any) {
        bindings.overlay.closeOverlay();
        const faq = Faq.fromObject({
            behavior: assistent.value.behavior,
            id: new FaqId(uuid()),
            question: faqForm.value.question,
            answer: faqForm.value.answer,
            createdAt: new Date(),
            updatedAt: new Date(),
        });
        assistent.value.behavior.faqs.push(faq);
        faqForm.value = {
            question: null,
            answer: null,
        };
        showNotification(
            'FAQ succesvol toegevoegd!',
            'Sla de assistent op om de wijzigingen door te voeren.',
        );
    }

    function deleteFaq(deletedFaqIndex: number): void {
        assistent.value.behavior.faqs.splice(deletedFaqIndex, 1);
    }

    function saveFaq(): void {
        showNotification(
            'FAQ is succesvol gewijzigd!',
            'Sla de assistent op om de wijzigingen door te voeren.',
        );
    }

    const showNotification = (title: string, text: string) => {
        notify({
            component: NotificationBlock,
            props: {
                color: 'green-500',
                title,
                text,
            },
            duration: 5000,
        });
    };

    defineExpose({
        faqForm,
        formValidation: exposedValidation,
    });

</script>

<style scoped lang="scss">
.faq-form {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    justify-content: center;
    align-items: center;
}

.faq-toggle {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}
</style>
